import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import CmsImage from '../components/CmsImage';
import { fetchLocalizedCollection, getLocalized } from '../lib/cms';

export default function PartnersPage() {
  const [cmsPartners, setCmsPartners] = useState([]);

  useEffect(() => {
    fetchLocalizedCollection('partnere_leverandoerer', 'da').then((items) => {
      if (items && items.length) {
        const filtered = items.filter((i) => {
          const kat = typeof i.kategori === 'object' ? getLocalized(i.kategori, 'da') : i.kategori;
          const hasTerm =
            i.terms &&
            i.terms['partner-kategori'] &&
            i.terms['partner-kategori'].includes('partner');
          return kat === 'Partner' || kat === 'partner' || hasTerm;
        });

        const uniqueMap = new Map();
        for (const item of filtered) {
          const rawName =
            typeof item.navn === 'object' ? getLocalized(item.navn, 'da') : item.navn;
          const key = (rawName || '').trim().toLowerCase();
          if (!key) continue;

          const existing = uniqueMap.get(key);
          if (!existing) {
            uniqueMap.set(key, item);
          } else {
            const itemTime = item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
            const existingTime = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
            if (itemTime > existingTime) {
              uniqueMap.set(key, item);
            }
          }
        }

        const sorted = Array.from(uniqueMap.values()).sort(
          (a, b) => (a.sortering || 0) - (b.sortering || 0)
        );

        if (sorted.length > 0) {
          setCmsPartners(
            sorted.map((p) => ({
              name: typeof p.navn === 'object' ? getLocalized(p.navn, 'da') : p.navn,
              desc: typeof p.beskrivelse === 'object' ? getLocalized(p.beskrivelse, 'da') : p.beskrivelse,
              img: Array.isArray(p.logo) ? p.logo[0] : p.logo,
              url: p.hjemmeside,
            }))
          );
        }
      }
    });
  }, []);

  const defaultPartners = [
    {
      name: 'Fredko Group',
      desc: 'Fredko Group bidrager med stærk teknisk viden, komponenter og leveringssikkerhed. Sammen leverer vi specialiserede løsninger til den nordiske metal- og pladeindustri.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fredko-final-1.webp',
      url: 'https://www.fredko.com/',
    },
    {
      name: 'PM Service',
      desc: 'PM Service leverer service, montage og teknisk support, så vores kunder altid er sikret maksimal oppetid og hurtig respons ude i den daglige drift.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pm-service-final-1.webp',
      url: 'https://pmservice.se/',
    },
  ];

  const partners = cmsPartners.length > 0 ? cmsPartners : defaultPartners;

  return (
    <>
      <Helmet>
        <title>PMB Partnere – Stærke samarbejder & industrielle løsninger</title>
        <meta
          name="description"
          content="PMB samarbejder med førende producenter og leverandører inden for presser, coil-linjer og centralsmøring. Sammen skaber vi driftssikre løsninger til den danske industri."
        />
        <meta property="og:title" content="PMB Partnere – Stærke samarbejder & industrielle løsninger" />
        <meta
          property="og:description"
          content="Fredko Group og PM Service er en naturlig del af vores partnerskab – hver med deres ekspertise, der komplementerer PMB’s løsninger."
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="hover:text-[#006368] transition">
            Forside
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">Om os</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">PMB Partnere</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              Strategiske Alliancer
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              Stærke partnere – stærkere løsninger
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              Hos <strong>PMB</strong> tror vi på, at de bedste resultater skabes i samspillet mellem erfaring, specialisering og fælles værdier.
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              Derfor samarbejder vi med udvalgte partnere, der deler vores fokus på kvalitet, driftssikkerhed og innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {partners.map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm hover:border-[#006368] transition duration-200 flex flex-col justify-between p-8"
              >
                <div>
                  <div className="h-52 sm:h-60 w-full flex items-center justify-center p-6 sm:p-8 bg-[#121619] rounded-lg border border-stone-800 mb-6 shadow-inner">
                    <CmsImage
                      src={p.img}
                      alt={p.name}
                      className="h-36 sm:h-44 w-auto max-w-[85%] object-contain filter brightness-105"
                      width={600}
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-900">{p.name}</h3>
                  <p className="mt-3 text-stone-700 text-sm sm:text-base leading-relaxed">
                    {p.desc}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-stone-100 flex items-center justify-between">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-[#006368] hover:bg-[#004d51] text-white text-xs font-semibold tracking-wide transition shadow-2xs"
                  >
                    <span>Besøg {p.name}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[11px]" />
                  </a>
                  <span className="text-xs text-stone-400 font-medium">Officiel PMB partner</span>
                </div>
              </div>
            ))}
          </div>

          {/* Statement box from scrape */}
          <div className="mt-16 p-8 sm:p-12 rounded-lg bg-stone-50 border border-stone-200 max-w-4xl mx-auto text-center space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
              Sammen skaber vi løsninger, der holder
            </h3>
            <p className="text-stone-700 text-base leading-relaxed max-w-2xl mx-auto">
              Sammen skaber vi løsninger, der ikke bare holder maskinerne i gang – men får hele produktionen til at køre bedre, længere og mere effektivt.
            </p>
            <p className="text-[#006368] font-semibold text-base leading-relaxed max-w-2xl mx-auto">
              Et partnerskab med PMB handler ikke kun om produkter. Det handler om mennesker, knowhow og en fælles passion for at levere kvalitet – hver eneste dag.
            </p>
            <div className="pt-4">
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-6 py-3 rounded bg-[#006368] text-white font-semibold text-sm hover:bg-[#004d51] transition shadow-sm"
              >
                <span>Tag en uforpligtende dialog</span>
                <i className="fa-solid fa-arrow-right text-xs" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
